import { apiClient, ApiResponse } from './api';
import { useAuthStore } from '@/store/auth.store';
import { useProfileStore } from '@/store/profile.store';
import type {
  CoverLetterGenerateRequest,
  CoverLetterGenerateResponse,
  CoverLetterContent,
  CoverLetterTemplate,
  GeneratedCoverLetter,
  CompanyResearchResult,
  CompanyResearchResponse,
} from '@shared/types/coverLetter';

export type CoverLetterExportFormat = 'pdf' | 'docx' | 'txt';

export interface CoverLetterExportResult {
  buffer: Blob;
  filename: string;
  mimeType: string;
}

/**
 * Cover letter generation timeout — AI pipeline with optional company research
 * can take up to 90 seconds.
 */
const GENERATE_TIMEOUT_MS = 120_000;
const RESEARCH_TIMEOUT_MS = 60_000;

export interface BackendCoverLetter {
  id: string;
  generatedAt: number;
  content: CoverLetterContent;
  letterText: string;
  template: CoverLetterTemplate;
  wordCount: number;
  atsKeywordCoverage: number;
  jobTitle?: string;
  company?: string;
  vacancyId?: string;
}

export const coverLetterService = {
  /**
   * List user's cover letters from backend (cloud mode)
   */
  async listCoverLetters(): Promise<BackendCoverLetter[]> {
    const response = await apiClient.get<ApiResponse<{
      coverLetters: BackendCoverLetter[];
      pagination: { total: number };
    }>>('/api/v1/cover-letter?limit=100');

    return response.data?.data?.coverLetters || [];
  },

  /**
   * Research a company website to gather context for the cover letter.
   */
  async researchCompany(websiteUrl: string): Promise<CompanyResearchResult> {
    const response = await apiClient.post<ApiResponse<CompanyResearchResponse>>(
      '/api/v1/company/research',
      { websiteUrl },
      { timeout: RESEARCH_TIMEOUT_MS }
    );
    const data = response.data.data;
    if (!data.success || !data.research) {
      throw new Error(data.error || 'Company research failed');
    }
    return data.research;
  },

  /**
   * Generate a cover letter for a specific job.
   * Sends profile data if user is not authenticated (local mode).
   */
  async generateCoverLetter(params: {
    vacancyData: CoverLetterGenerateRequest['vacancyData'];
    companyResearch?: CompanyResearchResult | null;
    options?: { template?: CoverLetterTemplate; tone?: 'formal' | 'professional' | 'casual' };
  }): Promise<GeneratedCoverLetter> {
    const { isAuthenticated } = useAuthStore.getState();

    const requestBody: CoverLetterGenerateRequest = {
      vacancyData: params.vacancyData,
      companyResearch: params.companyResearch ?? undefined,
      options: {
        template: params.options?.template ?? 'professional',
        tone: params.options?.tone ?? 'professional',
      },
    };

    // Include profile data for unauthenticated users
    if (!isAuthenticated) {
      const { profile } = useProfileStore.getState();
      if (!profile) {
        throw new Error('Profile not found. Please create a profile first.');
      }
      requestBody.profileData = profile as unknown as Record<string, unknown>;
    }

    const response = await apiClient.post<ApiResponse<CoverLetterGenerateResponse>>(
      '/api/v1/cover-letter/generate',
      requestBody,
      { timeout: GENERATE_TIMEOUT_MS }
    );

    const data = response.data.data;
    if (!data.success || !data.coverLetter) {
      throw new Error(data.error || 'Cover letter generation failed');
    }
    return data.coverLetter;
  },

  /**
   * Export a cover letter to PDF, DOCX, or TXT.
   */
  async exportCoverLetter(
    content: CoverLetterContent,
    letterText: string,
    format: CoverLetterExportFormat,
    template?: CoverLetterTemplate
  ): Promise<CoverLetterExportResult> {
    const response = await apiClient.post(
      '/api/v1/cover-letter/export-sections',
      {
        content,
        letterText,
        options: { format, template: template ?? 'professional' },
      },
      { responseType: 'blob', timeout: 60_000 }
    );

    const contentDisposition = response.headers['content-disposition'];
    let filename = `cover-letter.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    return {
      buffer: response.data,
      filename,
      mimeType: response.headers['content-type'],
    };
  },

  /**
   * Download a file blob to the user's device.
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
